import React from "react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { EmptyState, PALETTE, formatCurrency, formatMinutes, formatPercent } from "./Primitives";
import { formatHotel } from "../../../api/hotelTime";

function Section({ emoji, label, sub, items, background, borderColor, dividerColor, textColor, emptyMessage }) {
    return (
        <div
            className="print-keep"
            style={{
                marginBottom: 14,
                background,
                border: `1px solid ${borderColor}`,
                borderRadius: 12,
                overflow: "hidden",
            }}
        >
            <div
                style={{
                    padding: "10px 16px 9px",
                    borderBottom: `1px solid ${dividerColor}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                }}
            >
                <span style={{ fontSize: 14 }}>{emoji}</span>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: textColor }}>{label}</span>
                <span style={{ fontSize: 12, color: "#9097ac" }}>{sub}</span>
            </div>
            {items.length === 0 ? (
                <div style={{ padding: "14px 16px", fontSize: 12.5, color: "#9097ac" }}>{emptyMessage}</div>
            ) : (
                items.map((item, index) => (
                    <div
                        key={item.key}
                        style={{
                            padding: "11px 16px",
                            borderBottom: index < items.length - 1 ? `1px solid ${dividerColor}` : "none",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 12,
                        }}
                    >
                        <span style={{ fontSize: 18, flex: "0 0 auto", marginTop: 1 }}>{item.icon}</span>
                        <div>
                            <span style={{ fontSize: 13.5, fontWeight: 600, color: textColor }}>{item.metric}</span>
                            <span style={{ fontSize: 16, fontWeight: 700, color: textColor, marginLeft: 8 }}>
                                {item.value}
                            </span>
                            <span style={{ fontSize: 12, color: "#9097ac", marginLeft: 6 }}>{item.detail}</span>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
}

export default function WeeklyTab({ insights, weekOffset, onWeekChange }) {
    if (!insights) {
        return <EmptyState message="No report available" height={280} />;
    }

    const stats = insights.stats ?? {};

    const reportTitle = `Alfred Weekly Report ${formatHotel(insights.from, "DD MMM")} - ${formatHotel(
        insights.to,
        "DD MMM YYYY",
    )}`;

    const downloadPdf = () => {
        const previousTitle = document.title;
        const restore = () => {
            document.title = previousTitle;
            window.removeEventListener("afterprint", restore);
        };
        window.addEventListener("afterprint", restore);
        document.title = reportTitle;
        window.print();
    };

    const tiles = [
        {
            label: "Revenue",
            value: formatCurrency(stats.revenue),
            sub: `${formatCurrency(stats.upsell_revenue)} from upsells`,
            color: PALETTE.green,
        },
        {
            label: "Requests",
            value: stats.requests ?? 0,
            sub: `${formatPercent(stats.completion_rate)} completed`,
            color: PALETTE.blue,
        },
        {
            label: "Escalations",
            value: stats.escalations ?? 0,
            sub: stats.escalations === 0 ? "Clean week 🎉" : "Breached tiers",
            color: stats.escalations === 0 ? PALETTE.green : PALETTE.red,
        },
        {
            label: "Revenue Lost",
            value: formatCurrency(stats.revenue_lost),
            sub: "From declined requests",
            color: PALETTE.red,
        },
        {
            label: "Avg Closure",
            value: stats.avg_closure_minutes > 0 ? formatMinutes(stats.avg_closure_minutes) : "—",
            sub: "Across all types",
            color: PALETTE.amber,
        },
    ];

    return (
        <div className="print-root">
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: 16,
                    gap: 16,
                }}
            >
                <div>
                    <div
                        style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "#9097ac",
                            letterSpacing: 0.6,
                            marginBottom: 4,
                        }}
                    >
                        ALFRED WEEKLY REPORT
                    </div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "#1a1a2e" }}>
                        {formatHotel(insights.from, "DD MMM")} – {formatHotel(insights.to, "DD MMM YYYY")}
                    </div>
                    <div style={{ fontSize: 12.5, color: "#69708A", marginTop: 2 }}>
                        Compared against {formatHotel(insights.baseline_from, "DD MMM")} –{" "}
                        {formatHotel(insights.baseline_to, "DD MMM")} · generated{" "}
                        {formatHotel(insights.generated, "DD MMM YYYY, HH:mm")}
                    </div>
                </div>
                <div
                    className="print-hide"
                    style={{ display: "flex", gap: 8, flex: "0 0 auto", alignItems: "center" }}
                >
                    <button
                        onClick={() => onWeekChange(weekOffset - 1)}
                        style={navButtonStyle}
                        title="Previous week"
                    >
                        <CaretLeft size={14} />
                    </button>
                    <button
                        onClick={() => onWeekChange(weekOffset + 1)}
                        disabled={weekOffset >= 0}
                        style={{ ...navButtonStyle, opacity: weekOffset >= 0 ? 0.4 : 1 }}
                        title="Next week"
                    >
                        <CaretRight size={14} />
                    </button>
                    <button onClick={downloadPdf} style={{ ...navButtonStyle, width: "auto", padding: "8px 14px" }}>
                        Download PDF
                    </button>
                </div>
            </div>

            <div
                className="print-keep"
                style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 10, marginBottom: 14 }}
            >
                {tiles.map((tile) => (
                    <div
                        key={tile.label}
                        style={{
                            background: "#fff",
                            borderRadius: 10,
                            padding: "12px 14px",
                            border: "1.5px solid #ECEEF5",
                        }}
                    >
                        <div
                            style={{
                                fontSize: 9.5,
                                fontWeight: 700,
                                color: "#9097ac",
                                letterSpacing: 0.4,
                                marginBottom: 4,
                            }}
                        >
                            {tile.label.toUpperCase()}
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: tile.color }}>{tile.value}</div>
                        <div style={{ fontSize: 11, color: "#9097ac", marginTop: 2 }}>{tile.sub}</div>
                    </div>
                ))}
            </div>

            <Section
                emoji="🟢"
                label="Good"
                sub="— moved favourably against the 4-week baseline"
                items={insights.good ?? []}
                background="#F0FDF4"
                borderColor="#bbf7d0"
                dividerColor="#dcfce7"
                textColor="#15803d"
                emptyMessage="Nothing moved far enough above baseline to call out this week."
            />

            <Section
                emoji="🟡"
                label="Watch"
                sub="— moved unfavourably against the 4-week baseline"
                items={insights.watch ?? []}
                background="#FFFBEB"
                borderColor="#fde68a"
                dividerColor="#fef9c3"
                textColor="#92400e"
                emptyMessage="Nothing slipped meaningfully against baseline this week."
            />

            {(insights.urgent ?? []).length > 0 ? (
                <Section
                    emoji="🔴"
                    label="Urgent"
                    sub="— threshold breached, needs action"
                    items={insights.urgent}
                    background="#FFF0F0"
                    borderColor="#FFBFC1"
                    dividerColor="#F8D9DE"
                    textColor={PALETTE.accent}
                    emptyMessage=""
                />
            ) : (
                <div
                    className="print-keep"
                    style={{
                        marginBottom: 14,
                        padding: "13px 16px",
                        background: "#F0FDF4",
                        border: "1px solid #bbf7d0",
                        borderRadius: 12,
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: PALETTE.green,
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                    }}
                >
                    <span style={{ fontSize: 20 }}>🎉</span> No urgent issues this week
                </div>
            )}

            {(insights.low_volume_metrics ?? []).length > 0 && (
                <Section
                    emoji="⚪"
                    label="Held back"
                    sub="— too little volume this week to compare against baseline"
                    items={insights.low_volume_metrics}
                    background="#F8F9FC"
                    borderColor="#E4E7EF"
                    dividerColor="#ECEEF5"
                    textColor="#69708A"
                    emptyMessage=""
                />
            )}
        </div>
    );
}

const navButtonStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    width: 34,
    height: 34,
    background: "#fff",
    border: "1.5px solid #E4E7EF",
    borderRadius: 9,
    fontFamily: "inherit",
    fontSize: 12.5,
    fontWeight: 600,
    color: "#69708A",
    cursor: "pointer",
};
